import PageLayout from "@/components/PageLayout";
import { MessageSquare, Target, Zap, Shield, Users, Globe, Heart } from "lucide-react";
import { Link } from "react-router-dom";

const VALUES = [
  { icon: Shield, title: "Trust & Reliability", desc: "We uphold 99.9% uptime and honest, transparent pricing. No hidden fees, no surprises — ever.", color: "bg-[#eef1f8] text-[#0c1e41]" },
  { icon: Zap, title: "Speed & Performance", desc: "Messages reach all Nigerian networks within seconds. Our infrastructure is engineered for scale.", color: "bg-amber-50 text-amber-600" },
  { icon: Heart, title: "Customer First", desc: "Every feature we build starts with listening to Nigerian businesses. Your success is our metric.", color: "bg-rose-50 text-rose-600" },
  { icon: Globe, title: "Pan-Nigerian Focus", desc: "We understand local networks, local regulations, and local business needs better than anyone.", color: "bg-emerald-50 text-emerald-600" },
];

const MILESTONES = [
  { year: "2021", event: "Sendora founded in Lagos with a mission to democratise business messaging in Africa." },
  { year: "2022", event: "First 1,000 businesses onboarded. Launched bulk SMS, contact groups and campaign scheduling." },
  { year: "2023", event: "Expanded infrastructure to cover all 36 states. Introduced the REST API and developer SDKs." },
  { year: "2024", event: "Reached 5,000 active businesses. Launched Birthday SMS automation and the Contact Marketplace." },
  { year: "2025", event: "Introduced Team Management, enterprise subscription tiers, and Paystack payment integration." },
  { year: "2026", event: "Serving 10,000+ businesses. Continuing to build the future of Nigerian business communication." },
];

export default function About() {
  const seoProps = {
    title: "About Sendora — Nigeria's Premier Bulk SMS Company",
    description: "Learn how Sendora is powering business communication for 10,000+ Nigerian businesses since 2021. Our mission, values, and journey building Nigeria's most reliable SMS platform.",
    canonical: "/about",
    keywords: "about Sendora, Sendora Nigeria, bulk SMS company Nigeria, Nigerian SMS startup, Lagos tech company",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "url": "https://www.sendoras.com/about",
        "name": "About Sendora",
        "description": "Sendora's mission, values, team and company history.",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sendoras.com" },
            { "@type": "ListItem", "position": 2, "name": "About", "item": "https://www.sendoras.com/about" }
          ]
        }
      }
    ]
  };

  return (
    <PageLayout title="About Sendora" subtitle="Building Nigeria's most reliable business communication platform — one message at a time." seo={seoProps}>
      {/* Mission */}
      <section className="py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold text-[#0c1e41] bg-[#eef1f8] border border-[#c0cfe8] px-3 py-1 rounded-full uppercase tracking-wider">Our Mission</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-4 mb-4">
                Empowering Every Nigerian Business to Communicate at Scale
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Sendora was born from a simple observation: Nigerian businesses — from market traders to multinational banks — all need to reach their customers quickly and affordably. We built the platform we wished existed when we started.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Today, Sendora powers SMS communication for over 10,000 businesses across every industry and every state in Nigeria. We handle everything from one-off promotional blasts to complex, automated customer journeys.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "10,000+", label: "Active Businesses" },
                { value: "99.9%", label: "Uptime SLA" },
                { value: "4", label: "Networks Covered" },
                { value: "5 sec", label: "Avg. Delivery Time" },
              ].map(s => (
                <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-5 text-center shadow-sm">
                  <p className="text-2xl font-extrabold text-[#0c1e41]">{s.value}</p>
                  <p className="text-xs text-slate-500 mt-1 font-medium">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-extrabold text-slate-900">What We Stand For</h2>
            <p className="text-slate-500 mt-2">The principles that guide everything we build</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {VALUES.map(v => (
              <div key={v.title} className="flex gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${v.color}`}>
                  <v.icon size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">{v.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-extrabold text-slate-900">Our Journey</h2>
          </div>
          <div className="space-y-6">
            {MILESTONES.map((m, i) => (
              <div key={m.year} className="flex gap-5">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-[#0c1e41] text-white flex items-center justify-center text-xs font-extrabold shrink-0">{m.year}</div>
                  {i < MILESTONES.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 my-1" />}
                </div>
                <div className="pb-6">
                  <p className="text-sm text-slate-700 leading-relaxed">{m.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-[#0c1e41] to-[#060e1c] py-14 text-center">
        <div className="max-w-xl mx-auto px-4">
          <h2 className="text-2xl font-extrabold text-white mb-3">Join 10,000+ Nigerian Businesses</h2>
          <p className="text-[#adc0e0] mb-6 text-sm">Start free — no credit card required. Reach your customers in seconds.</p>
          <Link to="/register" className="inline-block bg-white text-[#0c1e41] font-semibold px-8 py-3 rounded-xl hover:bg-[#eef1f8] transition-colors shadow-lg text-sm">
            Create Free Account
          </Link>
        </div>
      </section>
    </PageLayout>
  );
}
